// shared/infrastructure/media.repository.js  (POST multipart at/media)
import Api from '../../../../api/global.service';
import { unwrap, firstItem } from './apiResponse';
import { PARENT_KEYS } from '../domain/asistentes';

const ENDPOINT = 'at/media';
// El backend nombra el archivo con time() (segundos): dos subidas en el mismo segundo se sobrescriben.
export const MIN_INTERVAL_MS = 1100;

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

// Cola global de subidas: garantiza >= MIN_INTERVAL_MS entre subidas aunque vengan de un guardado y de reintentos sueltos.
let cola = Promise.resolve();
let ultimaSubida = 0;

export const mediaRepository = {
  /** Sube un archivo y devuelve el item guardado ({ id, uuid, url_file, ... }) o null. Nunca envía estatus 3 (purgaría las imágenes guardadas). */
  async upload({ tipo, idPadre, uuid, creado, file }) {
    const form = new FormData();
    form.append('uuid', uuid);
    form.append(PARENT_KEYS[tipo], idPadre);
    form.append('creado', creado);
    form.append('estatus', 1);
    form.append('url_file_local', file);
    const body = await unwrap(Api.createMultiPart(ENDPOINT, form));
    const item = firstItem(body);
    return item && typeof item === 'object' ? item : null;
  },

  /**
   * Como `upload`, pero encolada: espera lo necesario para dejar >= 1100 ms desde la subida anterior (de cualquier origen).
   * `delay`/`now` inyectables para pruebas.
   */
  uploadEspaciado(args, { delay = sleep, now = Date.now } = {}) {
    const run = cola.then(async () => {
      const espera = ultimaSubida + MIN_INTERVAL_MS - now();
      if (espera > 0) await delay(espera);
      try {
        return await mediaRepository.upload(args);
      } finally {
        ultimaSubida = now();
      }
    });
    cola = run.catch(() => {});
    return run;
  },

  /** Elimina una imagen guardada (DELETE at/media/{uuid}, sin body). Irreversible: el backend borra el archivo. 404 -> DomainError. */
  async remove(uuid) {
    await unwrap(Api.delete(ENDPOINT, uuid));
  },

  /**
   * Sube [{ uuid, file }] de forma SECUENCIAL con >= 1100 ms entre subidas (workaround de la colisión por time()).
   * `delay` es inyectable para pruebas. `onProgress(done, total)`; `onUploaded(item)` tras cada éxito.
   */
  async uploadAll({ tipo, idPadre, items, creado, onProgress, onUploaded, delay = sleep }) {
    for (let i = 0; i < items.length; i++) {
      await mediaRepository.uploadEspaciado({ tipo, idPadre, uuid: items[i].uuid, creado, file: items[i].file }, { delay });
      if (onUploaded) onUploaded(items[i]);
      if (onProgress) onProgress(i + 1, items.length);
    }
  },
  /**
   * Sube [{ uuid, file }] en orden, UNA A UNA (misma espera), sin detenerse ante un fallo.
   * `onItem(item, estado, payload)`: 'uploading' | 'done' (payload = item guardado) | 'error' (payload = error).
   * Devuelve { subidas: [{ uuid, media }], fallidas: [{ uuid, error }] }.
   */
  async uploadEach({ tipo, idPadre, items, creado, onItem, delay = sleep }) {
    const subidas = [];
    const fallidas = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (onItem) onItem(item, 'uploading');
      try {
        const media = await mediaRepository.uploadEspaciado({ tipo, idPadre, uuid: item.uuid, creado, file: item.file }, { delay });
        subidas.push({ uuid: item.uuid, media });
        if (onItem) onItem(item, 'done', media);
      } catch (error) {
        fallidas.push({ uuid: item.uuid, error });
        if (onItem) onItem(item, 'error', error);
      }
    }
    return { subidas, fallidas };
  },
};

export default mediaRepository;
